import type { BrandTheme, PartialBrandTheme } from '../../types/BrandTheme'
import { brandTheme } from '../../types/BrandTheme'
import { merge } from 'theme-ui'
import { dirname } from 'path'
import {
  resolveFontFamily,
  resolveThemeUIFontSizes,
} from '@jotta/ui/FontConfig'

import './theme.css'

export const mediamarkttheme: BrandTheme = merge.all<
  BrandTheme,
  PartialBrandTheme
>(brandTheme, {
  branding: {
    brand: 'MEDIAMARKT',
    themedir: dirname(new URL(import.meta.url).pathname),
    staticDir: 'mediamarkt',
    height: 32,
    learnMoreLinkWaitlist: 8447570,
    learnMoreLinkSearch: 8447578,
  },
  fontSizes: resolveThemeUIFontSizes('mediamarkt'),
  fonts: resolveFontFamily('mediamarkt'),
  colors: {
    text: 'black',
    background: '#fff',
    primary: '#df0000',
    secondary: '#fff',
    accent: '#272422',
    accentText: '#fff',
    focus: '#272422',
    focusDark: '#fff',
    border: '#ddd',
    grayBg: '#4A4A4A',
    graySlateBg: '#45434C',
    darkGrayBg: 'rgba(69,67,76,.8)',
    lightBlueBg: '#4990E2',
    gray: '#45434c',
    photoSearchInactive: 'white',
  },
  buttons: {
    primary: {
      borderColor: 'accent',
      backgroundColor: 'accent',
      color: 'accentText',
    },
    accent: {
      backgroundColor: 'accent',
      color: 'accentText',
      borderColor: 'accent',
    },
    download: {
      backgroundColor: 'primary',
      textTransform: 'none',
      border: '2px solid transparent',
      '&:not(:disabled):hover': {
        border: '2px solid black',
        boxShadow: '0 0.5em 0.5em -0.4em rgba(0, 0, 0, 0.15)',
        transform: 'translateY(-0.25em)',
        opacity: 1,
      },
      ':focus': {
        border: '2px solid black',
        boxShadow: '0 0.5em 0.5em -0.4em rgba(0, 0, 0, 0.15)',
        transform: 'translateY(-0.25em)',
        opacity: 1,
      },
    },
    languageSelector: {
      borderRadius: '50%',
      border: '2px solid white',
    },
    openWebInterface: {
      border: '2px solid white',
      borderRadius: '8px',
      backgroundColor: 'primary',
      color: 'white !important',
      fontWeight: 'button',
      '&:not(:disabled):hover': {
        border: '2px solid white',
      },
    },
    fab: {
      backgroundColor: 'text',
      borderColor: 'text',
    },
    copyInputButton: {
      backgroundColor: 'text',
      borderColor: 'text',
      '--focus-color': 'var(--color-text)',
    },
    bellIcon: {
      color: 'background',
    },
    replyButton: {
      color: 'var(--color-text)',
    },
    replyButtonDarkMode: {
      color: 'background',
    },
  },
  forms: {
    input: {
      fontWeight: 'body',
      color: 'black',
      ':focus': { borderBottomColor: 'lightBlueBg' },
    },
    toggle: {
      '&:checked::before': {
        backgroundColor: 'accent',
      },
      '&:checked::after': {
        backgroundColor: 'accent',
      },
    },
    checkbox: {
      fontSize: '11px',
      borderRadius: '3px',
      border: '1px solid',
    },
  },
  grids: {
    rowDivider: {
      mt: '0 !important',
    },
  },
  layout: {
    appHeader: {
      backgroundColor: 'primary',
    },
    appHeaderContent: {
      color: 'background',
    },
    appHeaderHamburger: {
      color: 'white',
      '> svg': {
        color: 'white',
        opacity: 1,
        fontSize: '2em',
        width: '30px',
        m: 0,
      },
    },
    appHeaderDownload: {
      backgroundColor: 'primary',
      borderBottom: ['1px solid', 'none', 'none'],
      borderBottomColor: ['white', 'transparent', 'transparent'],
    },
    downloadPageContainer: {
      'h1, h2': {
        fontWeight: 'body',
      },
    },
    modalBackgroundPrimary: {
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
    },
  },
  cards: {
    photoUploadSuccess: {
      '--action-button-color': '#fff',
      '--action-button-text-opacity': 1,
      backgroundColor: 'text',
    },
    photoUpload: {
      '--action-button-text-opacity': 1,
    },
  },
  images: {
    familyPicture: {
      color: 'darkGrayBg',
    },
  },
  styles: {
    avatar: {
      borderRadius: '100%',
      border: '2px solid',
      borderColor: 'white',
      boxSizing: 'content-box',
    },
    h1: {
      fontWeight: 500,
      fontSize: '23px',
      color: 'grayBg',
      lineHeight: '18px',
      mb: 0,
    },
    familySettingHeading: {
      fontSize: 5,
      fontWeight: 500,
      margin: '0 0 20px',
      color: 'graySlateBg',
    },
    h2: {
      fontWeight: 500,
      margin: '0 0 20px',
      padding: 0,
      fontSize: '17px',
      color: 'graySlateBg',
    },
    h4: {
      fontWeight: 'bold',
      fontSize: 2,
    },
    p: {
      color: 'darkGrayBg',
      fontSize: 2,
    },
    boldParagraphStyle: {
      fontWeight: 'heading',
      fontSize: 2,
      color: 'darkGrayBg',
    },
    tr: {
      color: 'graySlateOpacityBg',
    },
    downloadPageHeader: {
      color: 'black',
    },
    downloadSvg: {
      color: 'black',
    },
    fileIcon: {
      '--file-icon-border-color': '#656565',
      '--folder-icon-fill': '#f8f8f8',
    },
    addMoreSvg: {
      color: 'text',
      height: 4,
      width: 4,
    },
    newAlbumP: {
      fontWeight: 500,
    },
    albumInfo: {
      color: 'gray',
      opacity: 0.8,
    },
    timelineHeading: {
      fontSize: '20px',
      fontWeight: 500,
    },
  },
})
export default mediamarkttheme
